<script setup>
/**
 * XN-4535 / XN-4536 — Policy lines.
 *
 * The kinds of coverage the agency sells. It had been in the sidebar, disabled,
 * while three other surfaces each kept their own hard-coded guess at what a
 * policy line is: the registration form, the policy picker on a record, and the
 * segment builder. This page is the list those three now read from, which is
 * why renaming a line here rewords all of them.
 *
 * The editor is a state of this page rather than a route of its own, matching
 * the prototype: the list is a handful of rows and going "into" one is not a
 * place you would deep-link or bookmark.
 */
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';

import Loading from '../Loading.vue';
import PageHeader from '../PageHeader.vue';
import PolicyLineEditor from '../policyLines/PolicyLineEditor.vue';
import PolicyLineIcon from '../policyLines/PolicyLineIcon.vue';
import PolicyLinePolicies from '../policyLines/PolicyLinePolicies.vue';
import CommsInfoBanner from '../communications/CommsInfoBanner.vue';
import IconChevronRight from '../icons/IconChevronRight.vue';
import IconPlus from '../icons/IconPlus.vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlEmptyState from '../xl/XlEmptyState.vue';
import XlStatusIcon from '../xl/XlStatusIcon.vue';
import { plural } from '../../helpers/plural.js';
import { COLORS, DEFAULT_ICON, UNTITLED } from '../../helpers/policyLines.js';
import { hubEmptyState } from '../../helpers/hubEmptyStates';
import { CAPABILITIES, can, hubReader } from '../../helpers/roles';
import { useAuthStore } from '../../stores/auth';
import { usePolicyLinesStore } from '../../stores/policyLines.js';

const COUPLING =
  'These lines are used in three other places: the policy-type question on your registration '
  + "form, the tags on each policyholder's record, and the segments in Communications. Rename one "
  + 'here and it changes in all three.';

const store = usePolicyLinesStore();

const api = () => window.store?.event_backend_api ?? '/api';

const isLoading = ref(true);
const isSaving = ref(false);
const selectedId = ref(null);
const expandedId = ref(null);
const editorRef = ref(null);

/**
 * A line being created, held here until it is saved.
 *
 * It used to be POSTed the moment New policy line was clicked, so that a
 * refresh could not lose it. The cost was worse than the risk: every click
 * somebody thought better of left an "Untitled policy line" row behind, and the
 * editor offered to Archive something that had never been created on purpose.
 * Nothing is written now until there is something worth writing.
 */
const drafting = ref(null);

const lines = computed(() => store.lines.filter((line) => !line.archived));

const selected = computed(() => drafting.value
  ?? lines.value.find((line) => line.id === selectedId.value)
  ?? null);

const holderCount = (line) => line?.holder_count;
const countKnown = (line) => typeof holderCount(line) === 'number';

/**
 * The sum of the per-line holder counts — so somebody holding both Home and
 * Auto contributes two. It is a count of held lines, not of policy records: a
 * person with two home policies still counts once, per the spec.
 *
 * The label calls it "policies in total", which is the prototype's own wording
 * and the story's AC4; the sentence beside it ("A person can hold several") is
 * what keeps that honest. Worth revisiting with design if a true policy count
 * is wanted, which would need a figure the hub does not hold.
 */
const totalPolicies = computed(() => lines.value.reduce((sum, line) => sum + (holderCount(line) ?? 0), 0));
const allCountsKnown = computed(() => lines.value.every(countKnown));

const notify = (message, error = false) => window.loadNotification?.({ message, error });

const load = async () => {
  await store.load({ force: true, withCounts: true });
  isLoading.value = false;
};

const openLine = (line) => {
  drafting.value = null;
  selectedId.value = line.id;
};

const closeEditor = () => {
  drafting.value = null;
  selectedId.value = null;
};

const authStore = useAuthStore();
// The owner and hub staff create and edit lines; archiving stays the owner's.
const canAuthor = computed(() => can(authStore.userRole, CAPABILITIES.MANAGE_POLICY_LINES, authStore.capabilities));
const canArchive = computed(() => can(authStore.userRole, CAPABILITIES.ARCHIVE_POLICY_LINES, authStore.capabilities));
const readerIsBroker = computed(() => hubReader(authStore) === 'broker');
const toggleLine = (line) => {
  expandedId.value = expandedId.value === line.id ? null : line.id;
};
const emptyState = computed(() => hubEmptyState('policyLines', {
  reader: hubReader(authStore),
  role: authStore.userRole,
  capabilities: authStore.capabilities,
  empty: !lines.value.length,
}));

/**
 * Open the editor on a line that does not exist yet. An owner who changes
 * nothing and goes back leaves no trace — Save stays inert until there is a
 * change, so there is nothing to write.
 */
const addLine = () => {
  selectedId.value = null;
  drafting.value = {
    id: null,
    name: '',
    icon: DEFAULT_ICON,
    color_hex: COLORS[0],
    archived: false,
    holder_count: 0,
  };
};

const saveLine = async (draft) => {
  isSaving.value = true;
  const creating = !selected.value?.id;

  try {
    const { data } = creating
      ? await axios.post(`${api()}/policy-line-tags`, draft)
      : await axios.put(`${api()}/policy-line-tags/${selected.value.id}`, draft);

    store.upsert(data?.data);

    const reach = holderCount(data?.data);
    notify(reach ? `Saved — updated for ${plural(reach, 'policyholder')}` : 'Saved');
    closeEditor();
  } catch (error) {
    notify(error.response?.data?.message ?? 'Could not save this policy line', true);
  } finally {
    isSaving.value = false;
  }
};

const archiveLine = async () => {
  const line = selected.value;
  if (!line) return;

  isSaving.value = true;

  try {
    await axios.post(`${api()}/policy-line-tags/${line.id}/archive`);

    store.remove(line.id);
    notify(`${line.name || UNTITLED} archived`);
    closeEditor();
  } catch (error) {
    notify(error.response?.data?.message ?? 'Could not archive this policy line', true);
  } finally {
    isSaving.value = false;
  }
};

// Leaving mid-edit with unsaved changes prompts: the editor reports them
// (useUnsavedChanges), and the router guard asks in the hub's own dialog.

onMounted(load);
</script>

<template>
  <PageHeader />

  <Loading v-if="isLoading" variant="rows" :rows="5" />

  <div v-else class="policy-lines" data-xl-gen="northstar">
    <PolicyLineEditor
      v-if="selected"
      ref="editorRef"
      :line="selected"
      :lines="lines"
      :holder-count="holderCount(selected)"
      :is-saving="isSaving"
      :can-archive="canArchive"
      @save="saveLine"
      @archive="archiveLine"
      @close="closeEditor"
    />

    <template v-else>
      <header class="policy-lines__head">
        <div class="policy-lines__heading">
          <h1>Policy lines</h1>
          <p>The kinds of coverage you sell. Each one is a name and a look — nothing else to fill in.</p>
        </div>
        <XlButton v-if="canAuthor" data-testid="new-policy-line" @click="addLine">
          <template #icon><IconPlus /></template>
          New policy line
        </XlButton>
      </header>

      <!-- The same banner Communications uses; it is generic despite the name. -->
      <CommsInfoBanner v-if="canAuthor" :text="COUPLING" />

      <XlEmptyState
        v-if="!lines.length && emptyState"
        variant="page"
        :icon="emptyState.icon"
        :headline="emptyState.headline"
        :body="emptyState.body"
        :actions="emptyState.actions"
        data-testid="policy-lines-empty"
        @action="addLine"
      />

      <template v-else-if="lines.length">
        <XlCard v-for="line in lines" :key="line.id" class="policy-lines__row-card">
          <button
            type="button"
            class="policy-lines__row"
            :aria-expanded="expandedId === line.id ? 'true' : 'false'"
            @click="toggleLine(line)"
          >
            <XlStatusIcon :color="line.color_hex" :size="58">
              <PolicyLineIcon :name="line.icon" :size="24" />
            </XlStatusIcon>

            <b class="policy-lines__name" :class="{ 'policy-lines__name--draft': !line.name }">
              {{ line.name || UNTITLED }}
            </b>

            <span class="policy-lines__count">
              <b>{{ countKnown(line) ? holderCount(line).toLocaleString() : '—' }}</b>
              <span>{{ plural(holderCount(line) ?? 0, 'policyholder').split(' ')[1] }}</span>
            </span>

            <IconChevronRight class="policy-lines__chev" :class="{ 'policy-lines__chev--open': expandedId === line.id }" />
          </button>

          <template v-if="expandedId === line.id">
            <div v-if="canAuthor" class="policy-lines__panel-bar">
              <XlButton variant="ghost" size="sm" data-testid="edit-line" @click="openLine(line)">Edit line</XlButton>
            </div>
            <PolicyLinePolicies :line-id="line.id" :scoped="readerIsBroker" @count="(n) => store.upsert({ ...line, holder_count: n })" />
          </template>
        </XlCard>

        <p class="policy-lines__total">
          {{ lines.length }} {{ lines.length === 1 ? 'line' : 'lines' }}
          <template v-if="allCountsKnown">· {{ plural(totalPolicies, 'policy', 'policies') }} in total. A person can hold several.</template>
        </p>
      </template>
    </template>
  </div>
</template>

<style lang="scss" scoped>
/* The same gutter every other page gets from .page-content, so the content
   starts where Overview's and Policyholders' does. The sidebar overlaps the
   page by 10px (HubShell), which made a 24px gutter read as 14. */
.policy-lines {
  padding: var(--setup-page-padding) var(--setup-page-padding) 60px;

  &__head {
    display: flex;
    align-items: flex-end;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }

  &__heading {
    flex: 1;
    min-width: 240px;

    h1 {
      margin: 0 0 6px;
      font-family: var(--xl-font-family);
      font-size: var(--xl-page-title-size);
      font-weight: 600;
      line-height: var(--xl-page-title-line);
      color: var(--xl-color-primary-text);
    }

    p {
      margin: 0;
      font-size: var(--xl-page-subtitle-size);
      line-height: var(--xl-page-subtitle-line);
      color: var(--xl-color-outline);
    }
  }

  &__row-card { margin-bottom: 12px; overflow: hidden; }

  &__row {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 14px 18px;
    border: 0;
    background: none;
    text-align: left;
    cursor: pointer;
    font-family: var(--xl-font-family);

    &:hover { background: var(--xl-color-surface-hover, rgba(20, 16, 24, 0.03)); }
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 16.5px;
    color: var(--xl-color-primary-text);

    &--draft { color: var(--xl-color-outline); font-style: italic; }
  }

  &__count {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    b { font-size: 15px; color: var(--xl-color-primary-text); }
    span { font-size: 12.5px; color: var(--xl-color-outline); }
  }

  &__chev { color: var(--xl-color-outline); flex: 0 0 auto; transition: transform 0.15s; }
  &__chev--open { transform: rotate(90deg); }

  &__panel-bar { display: flex; justify-content: flex-end; padding: 0 18px 8px; }

  &__total {
    margin: 14px 0 0;
    font-size: var(--xl-type-body-small-size, 13px);
    color: var(--xl-color-outline);
  }

  &__empty {
    max-width: 62ch;

    b { display: block; margin-bottom: 8px; font-size: var(--xl-type-title-small-size, 16px); }
    p { margin: 0 0 12px; color: var(--xl-color-outline); }
  }
}
</style>
